import { computed, inject, Injectable, signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { EmailAiActions, EmailAiSelectors } from '@core/email-ai-data';
import { EmailAiTypes, RejectionEmailTriggerContext } from '@core/email-ai-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { I18nService } from '@core/i18n-legacy';
import { DeprecatedIntelligenceSettingsService } from '@core/intelligence-logic';
import { createActionResponseFactory } from '@core/ngrx-legacy';
import { NotificationsController } from '@core/user-interface-legacy';
import { removeNodeFromHTML } from '@core/utils-legacy';

import { EmailAiAnalyticsService } from './email-ai-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class EmailAiGeneratorService {
  private readonly store = inject(Store);
  private readonly emailAiAnalytics = inject(EmailAiAnalyticsService);
  private readonly notificationService = inject(NotificationsController);
  private readonly intelligenceSettingsService = inject(DeprecatedIntelligenceSettingsService);
  private readonly featureSpecFacade = inject(FeatureSpecFacade);
  private readonly i18n = inject(I18nService);

  private readonly actionResponse = createActionResponseFactory(EmailAiActions);

  public readonly featureSpec = {
    enabled: this.featureSpecFacade.selectIsEnabled(FeatureCode.AiWriter),
    entitled: this.featureSpecFacade.selectIsEntitled(FeatureCode.AiWriter),
  };
  public readonly isEmailAiEnabled = computed(
    () =>
      this.featureSpec.enabled() &&
      !!this.intelligenceSettingsService.intelligenceSettings()?.aiWriterEnabled,
  );

  public isAiPromptVisible = signal(false);
  public isLoading = this.store.selectSignal(EmailAiSelectors.isPending);

  public response = this.store.selectSignal(EmailAiSelectors.data);
  public topic = signal<EmailAiTypes | null>(null);

  public onGenerateDraft(
    type: EmailAiTypes,
    candidateId: number,
    htmlBody: string,
    context: RejectionEmailTriggerContext,
  ) {
    if (type === EmailAiTypes.Rejection) {
      this.emailAiAnalytics.trackWriteRejectionEmail(context);
      this.actionResponse(
        EmailAiActions.fetchRejectionEmails,
        {
          payload: {
            id: candidateId!,
            template: this.getCurrentMessageHtmlWithoutSignature(htmlBody),
          },
        },
        {},
      ).subscribe(({ isSuccess }) => {
        if (!isSuccess) this.showErrorToast();
      });
    }
    this.isAiPromptVisible.set(true);
  }

  public closeMessageWindow() {
    this.isAiPromptVisible.set(false);
  }

  public onAiGenerate(prompt: string, candidateId: number, template?: string) {
    if (!!this.response()) {
      this.actionResponse(
        EmailAiActions.fetchContinueEmail,
        {
          payload: {
            id: this.response()!.metadata.chatId,
            lastMessage: this.response()!.content,
            answer: prompt,
          },
        },
        {},
      ).subscribe(({ isSuccess }) => {
        if (!isSuccess) this.showErrorToast();
      });
    } else {
      this.actionResponse(
        EmailAiActions.fetchCustomEmail,
        {
          payload: { id: candidateId, message: prompt, template },
        },
        {},
      ).subscribe(({ isSuccess }) => {
        if (!isSuccess) this.showErrorToast();
      });
    }
  }

  public getCurrentMessageHtmlWithoutSignature(htmlMessage: string): string | undefined {
    return removeNodeFromHTML(htmlMessage, '.recruitee-email-signature') || undefined;
  }

  public invalidateResponse() {
    this.store.dispatch(EmailAiActions.invalidateResponse());
  }

  public hideAiPrompt() {
    this.isAiPromptVisible.set(false);
    this.invalidateResponse();
  }

  public showAiPrompt() {
    this.isAiPromptVisible.set(true);
  }

  public setCurrentTopic(topic: EmailAiTypes) {
    this.topic.set(topic);
  }

  public showErrorToast() {
    this.notificationService.error({
      title: this.i18n.translate('app.redactor_inserts.ai_writer.notifications.ai_writer_error'),
    });
  }
}
